// @flow strict-local

import type {
  AdditionalSchedulerQuestion,
  SessionLink,
} from 'src/types/scheduler';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import map from 'lodash/map';

import * as api from 'src/utils/api-no-store';

import {Select} from 'src/components/lib/token-list-input';
import Checkbox from 'src/components/lib/checkbox';
// $FlowFixMe[untyped-import] error input generally tricky
import ErrorInput, {ErrorTextarea} from 'src/components/lib/error-input';
// $FlowFixMe[untyped-import] error list also tricky
import ErrorList from 'src/components/lib/error-list';

import {Button} from 'src/components/lib/new-button';
import {getEventConfirmationErrorMessagesMap} from './common.jsx';

import css from '../booking.css';

// TODO (kyle): we probably don't need this component anymore? all it does is add date/time
// to the resulting details object onPost.
export const ConfirmBookingDetails = ({
  appointment,
  onConfirmBooking,
  templateId, // unused
  additionalQuestions,
  color,
  session,
}: {
  templateId: string,
  appointment: ?{bookingDate: string, bookingTime: string},
  onConfirmBooking: ({
    name: string,
    email: string,
    phone: string,
    date: string,
    time: string,
    additionalQuestionAnswers: {question: string, answer: string | string[]}[],
  }) => mixed,
  additionalQuestions: AdditionalSchedulerQuestion[],
  color?: ?string,
  session?: SessionLink,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const handleSubmit = async (info) => {
    if (appointment?.bookingDate && appointment.bookingTime) {
      await onConfirmBooking({
        ...info,
        date: appointment.bookingDate,
        time: appointment.bookingTime,
      });
    } else {
      throw new Error('Cannot book without a booking time and date.');
    }
  };

  return (
    <BookingDetailsForm
      templateId={templateId}
      onSubmit={handleSubmit}
      additionalQuestions={additionalQuestions}
      color={color}
      session={session}
      buttonText={{
        init: t('Confirm', 'Confirm'),
        submitting: `${t('Confirming', 'Confirming')}...`,
        success: t('Confirmed', 'Confirmed'),
        error: t('Error', 'Error'),
      }}
      requirePhone
    />
  );
};

export function BookingDetailsForm({
  templateId,
  onSubmit,
  additionalQuestions = [],
  color,
  buttonText,
  requirePhone,
  session,
}: {
  templateId: string,
  onSubmit: ({
    name: string,
    email: string,
    phone: string,
    additionalQuestionAnswers: Array<{
      question: string,
      answer: string | string[],
    }>,
  }) => Promise<mixed>,
  additionalQuestions?: AdditionalSchedulerQuestion[],
  color?: ?string,
  buttonText: {
    init: string,
    submitting: string,
    success: string,
    error: string,
  },
  requirePhone?: boolean,
  session?: SessionLink,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [name, setName] = React.useState(session?.guest_name ?? '');
  const [email, setEmail] = React.useState(session?.guest_email ?? '');
  const [phone, setPhone] = React.useState(session?.guest_phone ?? '');

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitState, setSubmitState] = React.useState<
    'init' | 'submitting' | 'success' | 'error',
  >('init');

  const [additionalQuestionResponses, setAdditionalResponse] = React.useState(
    Object.fromEntries(
      additionalQuestions.map((q) => {
        const sessionValue = session?.additional_question_answers.find(
          (answer) => answer.question === q.question,
        )?.answer;

        let defaultValue;
        switch (q.answer_type) {
          case 'number':
            defaultValue = '0';
            break;
          case 'checkbox':
            defaultValue = false;
            break;
          case 'consent':
            defaultValue = undefined;
            break;
          case 'dropdown':
            defaultValue = q.choices[0];
            break;
          default:
            defaultValue = '';
        }

        return [q.question, sessionValue ?? defaultValue];
      }),
    ),
  );

  const [errors, setError] = React.useState<{
    [string]: Array<string> | void,
  }>(
    Object.fromEntries(additionalQuestions.map((q) => [q.question, undefined])),
  );

  const errorMessagesMap = getEventConfirmationErrorMessagesMap(t);
  const validateDetails = () => {
    const reqd = {guest_name: name, guest_email: email};
    const reqdEntries = Object.entries(reqd);
    const extraEntries = Object.entries(additionalQuestionResponses);

    const entries = [...reqdEntries, ...extraEntries];
    const nextErrors = {...errors};

    if (requirePhone) {
      entries.push(['guest_phone', phone]);
    }

    entries.forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim().length === 0) {
        nextErrors[key] = [
          t(
            'This_question_requires_a_response',
            'This question requires a response',
          ),
        ];
      }
      if (value === undefined) {
        nextErrors[key] = [
          t(
            'This_question_requires_a_response',
            'This question requires a response',
          ),
        ];
      }
    });

    // We stop here and complain about unfilled values or any other errors
    // that exist in the erorr object
    if (Object.values(nextErrors).filter(Boolean).length > 0) {
      setError(nextErrors);
      return false;
    }

    return true;
  };

  const validateQuestion = React.useCallback(
    async (question: string, answer: string | number) => {
      if (typeof answer === 'string' && answer.trim().length === 0) {
        setError({
          ...errors,
          [question]: [
            t(
              'This_question_requires_a_response',
              'This question requires a response',
            ),
          ],
        });
        return;
      } else {
        setError({...errors, [question]: undefined});
      }
      const payload = {additional_question_answers: []};
      const isGuestDetail = [
        'guest_email',
        'guest_phone',
        'guest_name',
      ].includes(question);
      if (isGuestDetail) {
        payload[question] = answer;
      } else {
        payload['additional_question_answers'].push({question, answer});
      }
      const response = await api.post(
        `scheduler/event-template/${templateId}/validate-responses`,
        payload,
      );
      if (response.invalid) {
        const currentErrorCode = isGuestDetail
          ? response.invalid[question]
          : response.invalid.additional_question_answers[question];

        const currentError = errorMessagesMap[currentErrorCode];

        if (currentError) {
          setError({...errors, [question]: [currentError]});
        } else {
          setError({...errors, [question]: undefined});
        }
      }
    },
    [errors],
  );

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    if (Object.values(errors).some((error) => error !== undefined)) {
      return true;
    }

    setSubmitState('submitting');
    setIsSubmitting(true);
    const isOk = validateDetails();

    if (!isOk) {
      setSubmitState('init');
      setIsSubmitting(false);
      return; // the ui has errored
    }

    setSubmitState('submitting');
    try {
      await onSubmit({
        name,
        email,
        phone,
        additionalQuestionAnswers: map(
          additionalQuestionResponses,
          // $FlowFixMe[incompatible-call]
          (value, key) => ({question: key, answer: value}),
        ),
      });
      setSubmitState('success');
      setIsSubmitting(false);
    } catch (err) {
      if (!err.handled) {
        setSubmitState('error');
        setIsSubmitting(false);
      }
    }
  };

  const valueAsSelectOption = (value) => ({label: value, value});

  return (
    <div className={css.confirmBox}>
      <form className={css.confirmForm} onSubmit={handleSubmit}>
        <label className={css.confirmLabel}>
          <span>{t('Name', 'Name')}</span>
          <ErrorInput
            className={css.fullWidth}
            placeholder={t('Jana_Ally', 'Jana Ally')}
            value={name}
            onChange={(evt) => setName(evt.currentTarget.value)}
            onBlur={(evt) => {
              if (evt.currentTarget.value.trim().length === 0) {
                setError({
                  ...errors,
                  ['guest_name']: [
                    t(
                      'This_question_requires_a_response',
                      'This question requires a response',
                    ),
                  ],
                });
              } else {
                setError({...errors, ['guest_name']: undefined});
              }
            }}
            errors={errors['guest_name']}
          />
        </label>

        <label className={css.confirmLabel}>
          <span>{t('Email', 'Email')}</span>
          <ErrorInput
            className={css.fullWidth}
            placeholder={t('janacompany', 'jana@compa.ny')}
            type="email"
            value={email}
            onChange={(evt) => setEmail(evt.currentTarget.value)}
            onBlur={(evt) =>
              validateQuestion('guest_email', evt.currentTarget.value)
            }
            errors={errors['guest_email']}
          />
        </label>

        <label className={css.confirmLabel}>
          <span>{t('Phone', 'Phone')}</span>
          <ErrorInput
            className={css.fullWidth}
            type="tel"
            placeholder="999-999-9999"
            value={phone}
            onChange={(evt) => setPhone(evt.currentTarget.value)}
            onBlur={
              requirePhone
                ? (evt) =>
                    validateQuestion('guest_phone', evt.currentTarget.value)
                : null
            }
            errors={errors['guest_phone']}
          />
        </label>

        {additionalQuestions.map(({question, answer_type, choices}) => (
          <React.Fragment key={question}>
            {answer_type === 'email' && (
              <label className={css.confirmLabel}>
                <span>{question}</span>
                <ErrorInput
                  className={css.fullWidth}
                  type="email"
                  placeholder={t('janacompany', 'jana@compa.ny')}
                  value={additionalQuestionResponses[question] || ''}
                  onChange={(evt) =>
                    setAdditionalResponse({
                      ...additionalQuestionResponses,
                      [question]: evt.currentTarget.value,
                    })
                  }
                  onBlur={(evt) =>
                    validateQuestion(question, evt.currentTarget.value)
                  }
                  errors={errors[question]}
                />
              </label>
            )}

            {answer_type === 'phone' && (
              <label className={css.confirmLabel}>
                <span>{question}</span>

                <ErrorInput
                  className={css.fullWidth}
                  type="tel"
                  placeholder="999-999-9999"
                  value={additionalQuestionResponses[question] || ''}
                  onChange={(evt) =>
                    setAdditionalResponse({
                      ...additionalQuestionResponses,
                      [question]: evt.currentTarget.value,
                    })
                  }
                  onBlur={(evt) =>
                    validateQuestion(question, evt.currentTarget.value)
                  }
                  errors={errors[question]}
                />
              </label>
            )}

            {answer_type === 'number' && (
              <label className={css.confirmLabel}>
                <span>{question}</span>

                <ErrorInput
                  className={css.fullWidth}
                  type="number"
                  placeholder="9.99"
                  value={additionalQuestionResponses[question] || ''}
                  onChange={(evt) =>
                    setAdditionalResponse({
                      ...additionalQuestionResponses,
                      [question]: evt.currentTarget.value,
                    })
                  }
                  onBlur={(evt) =>
                    validateQuestion(question, evt.currentTarget.value)
                  }
                  errors={errors[question]}
                />
              </label>
            )}

            {answer_type === 'multi_line_text' && (
              <label className={css.confirmLabel}>
                <span>{question}</span>

                <ErrorTextarea
                  className={css.multiLineErrorInput}
                  value={additionalQuestionResponses[question] || ''}
                  onChange={(evt) =>
                    setAdditionalResponse({
                      ...additionalQuestionResponses,
                      [question]: evt.currentTarget.value,
                    })
                  }
                  onBlur={(evt) => {
                    if (evt.currentTarget.value.trim().length === 0) {
                      setError({
                        ...errors,
                        [question]: [
                          t(
                            'This_question_requires_a_response',
                            'This question requires a response',
                          ),
                        ],
                      });
                    } else {
                      setError({...errors, [question]: undefined});
                    }
                  }}
                  errors={errors[question]}
                />
              </label>
            )}

            {answer_type === 'checkbox' && (
              <div className={css.confirmLabel}>
                <label className={css.checkboxLabel}>
                  <Checkbox
                    checked={!!additionalQuestionResponses[question]}
                    onChange={() => {
                      setAdditionalResponse({
                        ...additionalQuestionResponses,
                        [question]: !additionalQuestionResponses[question],
                      });
                    }}
                  />
                  <span className={css.checkboxChoice}>{question}</span>
                </label>
              </div>
            )}

            {answer_type === 'consent' && (
              <div className={css.confirmLabel}>
                <label className={css.checkboxLabel}>
                  <Checkbox
                    checked={!!additionalQuestionResponses[question]}
                    onChange={() => {
                      setAdditionalResponse({
                        ...additionalQuestionResponses,
                        [question]: !additionalQuestionResponses[question],
                      });
                      if (!additionalQuestionResponses[question] !== true) {
                        setError({
                          ...errors,
                          [question]: [
                            t(
                              'This_question_requires_a_response',
                              'This question requires a response',
                            ),
                          ],
                        });
                      } else {
                        setError({...errors, [question]: undefined});
                      }
                    }}
                  />
                  <span className={css.checkboxChoice}>{question}</span>
                </label>
                {errors[question] && (
                  <div className={css.errorList}>
                    <ErrorList errors={errors[question]} />
                  </div>
                )}
              </div>
            )}

            {answer_type === 'dropdown' && (
              <div className={css.confirmLabel}>
                <span>{question}</span>
                <Select
                  classNames={{container: css.fullWidth}}
                  value={
                    additionalQuestionResponses[question]
                      ? valueAsSelectOption(
                          additionalQuestionResponses[question],
                        )
                      : undefined
                  }
                  options={choices.map(valueAsSelectOption)}
                  onChange={(val) =>
                    setAdditionalResponse({
                      ...additionalQuestionResponses,
                      [question]: val?.value,
                    })
                  }
                />
              </div>
            )}
          </React.Fragment>
        ))}

        <Button
          type="primary"
          disabled={isSubmitting}
          onClick={handleSubmit}
          color={color}
        >
          {buttonText[submitState] || t('Submit', 'Submit')}
        </Button>
        <button style={{display: 'hidden'}} />
      </form>
    </div>
  );
}
